import { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";

import { cases } from "@api/index";
import { PAYMENT_CALLBACK_COMPLETE } from "@types-runway/billing";
import { PAY_CASE } from "@store/cases/casesActions";
import { APP_URL, ENV } from "@utils/constantsEnv";

interface UsePaymentCompletionProps {
  caseId: number;
  redirectPath?: string;
}

export const usePaymentCompletion = ({
  caseId,
  redirectPath = "/thank_you",
}: UsePaymentCompletionProps) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const verifyPaymentStatus = useCallback(async () => {
    if (!caseId) return;

    try {
      setIsVerifying(true);
      setError(null);

      // 1. Call your backend
      const { data } = await cases.caseGet(`/${caseId}/invoice`);

      // 2. Check generic status
      if (data.paid) {
        // Process payment case automatically through server.
        dispatch({
          type: PAY_CASE,
          newCaseId: caseId,
        });
        history.push(redirectPath);
      } else {
        // Handle edge case: Payment "finished" but webhook hasn't processed yet
        setError("Payment received, but status is still pending. Please wait.");
      }
    } catch (err) {
      console.error("Verification failed", err);
      setError("Could not verify payment status.");
    } finally {
      setIsVerifying(false);
    }
  }, [caseId, history, redirectPath]);

  // The Event Listener
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // SECURITY: Validate origin before processing any postMessage events
      // This prevents malicious websites from triggering fake payment completions
      if (event.origin !== APP_URL && ENV !== "development") {
        console.warn(
          `[Security] Rejected postMessage from untrusted origin: ${event.origin}. Expected: ${APP_URL}`
        );
        return;
      }

      // Filter Protocol
      if (event.data?.type === PAYMENT_CALLBACK_COMPLETE) {
        verifyPaymentStatus();
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [verifyPaymentStatus]);

  return {
    isVerifying,
    error,
    verifyPaymentStatus,
  };
};
